/**
 * ModifierCard
 * Inline modifier picker rendered directly in the chat area when an item
 * with required options is added. Supports multiple option groups.
 * All state is local — no global store interaction until the user confirms.
 */

import { Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';

// ─── Types (exported for ChatWidget) ─────────────────────────────────────────

export interface PickerOption {
  option_id: string;
  group_id: string;
  label: string;
  price_adjustment?: number;
}

// options in the group — note group_id is optional here (it's the parent's id)
export interface GroupOption {
  option_id: string;
  label: string;
  price_adjustment?: number;
}

export interface OptionGroup {
  group_id: string;
  group_name: string;
  required: boolean;
  options: GroupOption[];
}

export interface PendingModifierItem {
  groups: OptionGroup[];
  pendingItem: { menu_item_id: string; name: string; unit_price: number };
  quantity: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ModifierCardProps {
  groups: OptionGroup[];
  itemName: string;
  quantity?: number;
  language: 'en' | 'ar';
  onConfirm: (selections: PickerOption[]) => void;
  disabled?: boolean;
}

export default function ModifierCard({
  groups,
  itemName,
  quantity = 1,
  language,
  onConfirm,
  disabled = false,
}: ModifierCardProps) {
  const theme = useTheme();
  const isRTL = language === 'ar';

  // selections: group_id → chosen PickerOption
  const [selections, setSelections] = useState<Record<string, PickerOption>>({});

  const requiredGroupIds = groups.filter((g) => g.required).map((g) => g.group_id);
  const allRequiredSelected = requiredGroupIds.every((id) => id in selections);

  const handlePick = (group: OptionGroup, opt: GroupOption) => {
    if (disabled) return;
    // Enrich with group_id so callers can build CartModifier
    const pickerOption: PickerOption = { ...opt, group_id: group.group_id };
    setSelections((prev) => ({ ...prev, [group.group_id]: pickerOption }));
  };

  const handleConfirm = () => {
    if (!allRequiredSelected || disabled) return;
    onConfirm(Object.values(selections));
  };

  const addLabel = language === 'ar'
    ? quantity > 1 ? `أضف ${quantity} إلى السلة` : 'أضف إلى السلة'
    : quantity > 1 ? `Add ${quantity} to Cart` : 'Add to Cart';

  return (
    <Box
      sx={{
        mt: 0.75,
        p: 1.5,
        borderRadius: 2,
        border: '1.5px solid',
        borderColor: disabled ? 'divider' : 'primary.light',
        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper',
        direction: isRTL ? 'rtl' : 'ltr',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, fontSize: '0.73rem', mb: 1, display: 'block', color: 'text.secondary' }}
      >
        {itemName}{quantity > 1 ? ` × ${quantity}` : ''}
      </Typography>

      <Stack gap={1.25}>
        {groups.map((group) => (
          <Box key={group.group_id}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', mb: 0.5, display: 'block' }}
            >
              {group.group_name}
              {group.required && (
                <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
              )}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {group.options.map((opt) => {
                const isChosen = selections[group.group_id]?.option_id === opt.option_id;
                const isLocked = disabled;

                return (
                  <ButtonBase
                    key={opt.option_id}
                    onClick={() => handlePick(group, opt)}
                    disabled={isLocked}
                    sx={{
                      borderRadius: 5,
                      px: 1.5,
                      py: 0.6,
                      border: '1.5px solid',
                      borderColor: isChosen ? 'primary.main' : 'primary.light',
                      bgcolor: isChosen ? 'primary.main' : 'background.paper',
                      color: isChosen ? 'white' : 'primary.main',
                      transition: 'all 0.15s',
                      cursor: isLocked ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      boxShadow: !isLocked && !isChosen
                        ? `0 1px 4px ${theme.palette.primary.main}30`
                        : 'none',
                      '&:hover': !isLocked
                        ? { bgcolor: isChosen ? 'primary.dark' : 'primary.50', borderColor: 'primary.main' }
                        : {},
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: 1, userSelect: 'none' }}>
                      {opt.label}
                    </Typography>
                    {opt.price_adjustment !== undefined && opt.price_adjustment !== 0 && (
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8, lineHeight: 1, userSelect: 'none' }}>
                        {opt.price_adjustment > 0 ? '+' : ''}£{opt.price_adjustment.toFixed(2)}
                      </Typography>
                    )}
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <Button
        size="small"
        variant="contained"
        disabled={!allRequiredSelected || disabled}
        onClick={handleConfirm}
        sx={{ mt: 1.25, borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}
      >
        {addLabel}
      </Button>
    </Box>
  );
}
